import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import PageLayout from "../_layout/PageLayout";
import {
  ManageDeletedData,
  ViewLinkedSewerObjects,
  ViewLinkedSewerObjectsFromAssignedProject,
} from "../../services/authorization/Permissions";
import DataGrid from "../../components/datatable/DataGrid";
import useUrlParams from "../../services/url/UrlParamsService";
import { LinkedObjectsStorageModel } from "../../services/sewer-object/LinkedObjectsStorage";
import { api } from "../../services/api/ApiService";
import {
  ActionsSection,
  ButtonsSection,
} from "../../components/data-components/sections/DataSections";
import DeleteButton from "../../components/buttons/delete-button/DeleteButton";
import { BadRequestError } from "../../services/api/BadRequestError";
import useErrorHandler, {
  ApplicationError,
} from "../../services/error-handler/ErrorHandlerService";
import { CurrentStructureStorageModel } from "../../services/sewer-object/CurrentStructureStorage";
import { StructureMenuItemsModel } from "./structure-details/StructureMenuItems";
import { permissionsService } from "../../services/authorization/UserPermissionsService";
import { useHistory } from "react-router-dom";
import useRouteService from "../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../services/breadcrumbs/BreadcrumbsStorage";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar, handleErrorInModal } = useErrorHandler();
  const { isUserOnProjects } = useRouteService();

  const structureId = useMemo(() => urlParamsService.getStructureId()!, [urlParamsService]);

  const [currentStructureStorage] = useState(() =>
    CurrentStructureStorageModel.create({
      structureId: structureId,
      parentEntityFromPath: {
        id: urlParamsService.getSewerObjectParentId()!,
        type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job
      }
    }, { api })
  );

  const [linkedObjectsStorage] = useState(() =>
    LinkedObjectsStorageModel.create(
      {
        sewerObjectId: structureId,
        useGridFiltering: true,
      },
      { api }
    )
  );

  const menuItems = useMemo(() => {
    const structureMenuItemsStorage = StructureMenuItemsModel.create({
      structureId: currentStructureStorage.structureId,
      parentIdFromPath: currentStructureStorage.parentEntityFromPath.id
    });

    return {
      staticItems: structureMenuItemsStorage.getStaticMenuItems(),
    };
  }, [currentStructureStorage.parentEntityFromPath.id, currentStructureStorage.structureId]);

  const deleteSelectedSewerObjects = useCallback(async () => {
    linkedObjectsStorage.setIsDeletingSewerObjects(true);
    try {
      await linkedObjectsStorage.deleteSewerObjects(linkedObjectsStorage.selectedItems);
    } catch (error) {
      const applicationError = error as ApplicationError;

      if (applicationError as BadRequestError) {
        applicationError.withModalTitleKey("general.sewer-objects.delete-error.title");
        handleErrorInModal(applicationError);
      } else {
        applicationError.withErrorMessage("default-error.sewer-objects.delete-from-project");
        handleErrorInSnackbar(applicationError);
      }
    }
    linkedObjectsStorage.setIsDeletingSewerObjects(false);
  }, [handleErrorInModal, handleErrorInSnackbar, linkedObjectsStorage]);

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([{
        entity: currentStructureStorage.parentEntityFromPath.type,
        name: await currentStructureStorage.getParentEntityName(currentStructureStorage.currentStructure.project.name)

        }, {
        entity: EntityTypeFromPath.SewerObject,
        name: currentStructureStorage.currentStructure.name
        }
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [currentStructureStorage, currentStructureStorage.currentStructure, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewLinkedSewerObjects, ViewLinkedSewerObjectsFromAssignedProject]}
      titleKey="ViewStructureLinkedObjects.title"
      titleContext={{ structureName: currentStructureStorage.currentStructure.name }}
      id="view-structure-linked-objects"
      menuItems={menuItems}
      isLoading={currentStructureStorage.isLoading}
    >
      <ActionsSection>
        <ButtonsSection>
          <DeleteButton
            disabled={linkedObjectsStorage.isDeleteDisabled}
            confirmationAction={deleteSelectedSewerObjects}
            confirmationModalTitleKey="ProjectSewerObjects.delete.confirmation.title"
            confirmationModalTitleContext={{
              sewerObjectsCount: linkedObjectsStorage.selectedItemsCount,
            }}
            confirmationModalSubtitleKey="ProjectSewerObjects.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
        </ButtonsSection>
      </ActionsSection>
      <DataGrid
        itemTypeKey="general.linked-objects"
        dataGridStorage={linkedObjectsStorage}
        noResultsMessageKey="general.list.no-linked-objects"
      />
    </PageLayout>
  );
});
